// frontend/shared-ui/src/components/AvatarUploader.jsx
import React, { useRef, useState } from "react";
import Spinner from "./Spinner";

/**
 * Circular avatar with click-to-upload.
 * Props:
 *   value        — current image URL or null
 *   onChange     — (newUrl) => void
 *   onUpload     — (file) => Promise<string>   // returns uploaded URL
 *   size         — px (default 96)
 *   fallback     — initials or emoji to show when no image (default "?")
 *   accept       — file types (default "image/*")
 */
export default function AvatarUploader({
  value,
  onChange,
  onUpload,
  size = 96,
  fallback = "?",
  accept = "image/*",
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handlePick = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const url = await onUpload?.(file);
      if (url) onChange?.(url);
    } catch (err) {
      setError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="fh-avatar-uploader">
      <button
        type="button"
        onClick={handlePick}
        className="fh-avatar-uploader__circle"
        style={{ width: size, height: size }}
        disabled={disabled || uploading}
      >
        {uploading ? (
          <Spinner size="md" />
        ) : value ? (
          <img src={value} alt="Avatar" />
        ) : (
          <span className="fh-avatar-uploader__fallback">{fallback}</span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {error && <div className="fh-avatar-uploader__error">{error}</div>}
    </div>
  );
}
